<script setup lang="ts">
import { formatValue } from '@/utils/helpers';

const TITLE = 'Properties';
const EMPTY_MESSAGE = 'Properties empty';

type Props = {
    isLoading?: boolean;
    initialVisibility?: boolean;
    properties?: AppWTFNft['properties'];
};

const props = defineProps<Props>();
const data = computed(() => !props.isLoading
    ? props.properties
    : Array(3).fill({ name: '', value: '', rarity: 1 }));

const isUrl = (str: string): boolean => {
    try {
        new URL(str);
        return true;
    }
    catch {
        return false;
    }
};

</script>

<template>
  <TokenDropPanel :title="TITLE"
                  :initial-visibility="initialVisibility">
    <div v-if="data && data.length > 0"
         class="display-flex align-items-start justify-center flex-column">
      <div class="traits-container">
        <div v-for="trait in data"
             :key="trait.name"
             class="display-flex flex-column gap-4 traits-container__card"
        >
          <p class="text-400-default text-color-secondary">
            <template v-if="props.isLoading">
              <SkeletonMain class="traits-container__skeleton" />
            </template>
            <template v-else>
              {{ trait.name }}
            </template>
          </p>

          <p class="compact-500-accent overflow-hidden cursor-default trait-value">
            <template v-if="props.isLoading">
              <SkeletonMain class="traits-container__skeleton" />
            </template>
            <template v-else>
              <a v-if="trait.value && isUrl(trait.value)"
                 :href="trait.value"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="text-link cursor-pointer">
                {{ trait.value }}
              </a>
              <template v-else>
                {{ trait.value }}
              </template>
            </template>
          </p>
          <p v-if="trait.rarity"
             class="text-400-default text-color-secondary">
            <template v-if="props.isLoading">
              <SkeletonMain class="traits-container__skeleton" />
            </template>
            <template v-else>
              {{ formatValue(trait.rarity, {precision: 2}) }}% have this
            </template>
          </p>
        </div>
      </div>
    </div>
    <div v-else
         class="display-flex align-items-center align-self-center justify-center flex-column"
    >
      <IconMain :size="40"
                icon="air"
      />
      <p>{{EMPTY_MESSAGE}}</p>
    </div>
  </TokenDropPanel>
</template>
<style lang="scss" scoped>
.traits-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  align-self: stretch;
  gap: 10px;

  &__card {
    flex: 0 1 auto;
    padding: 8px 16px;
    transition: border 0.3s ease;
    border: 1px solid var(--border-primary, rgb(233 234 236 / 20%));
    border-radius: 2px;
    cursor: default;

    & .trait-value {
      text-overflow: ellipsis;
    }

    &:hover {
      border: 1px solid var(--border-brand-aviation);
    }
  }

  &__skeleton {
    display: block;
    flex: 1;
    width: 100%;
    min-width: 100px;
    height: 24px;
  }
}
</style>
